/// <amd-module name="Core/Medius.Core.Web/Scripts/components/readonlyTabs/lines"/>
import * as React from 'react';
import { IDocumentImportInformation, LinesDto } from "../interfaces";
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { DataTable, ExpandChangeEvent, FixedGridLayout, LockedField, Text } from '@medius/ui-controls';

interface LinesProps {
    information: IDocumentImportInformation;
}

export const isAnyLinesNotEmpty = (information: IDocumentImportInformation): boolean =>
    information?.lines !== null && information?.lines !== undefined && information.lines.length > 0;

const renderDetails = (row: LinesDto) => {
    const customFields = [
        { value: row.textCustomField1Value, name: row.textCustomField1Name },
        { value: row.numericCustomField1Value?.toString(), name: row.numericCustomField1Name },
        { value: row.booleanCustomField1Value?.toString(), name: row.booleanCustomField1Name },
        { value: row.dateTimeCustomField1Value && new Date(row.dateTimeCustomField1Value).toLocaleDateString(), name: row.dateTimeCustomField1Name },

        { value: row.textCustomField2Value, name: row.textCustomField2Name },
        { value: row.numericCustomField2Value?.toString(), name: row.numericCustomField2Name },
        { value: row.booleanCustomField2Value?.toString(), name: row.booleanCustomField2Name },
        { value: row.dateTimeCustomField2Value && new Date(row.dateTimeCustomField2Value).toLocaleDateString(), name: row.dateTimeCustomField2Name },

        { value: row.textCustomField3Value, name: row.textCustomField3Name },
        { value: row.numericCustomField3Value?.toString(), name: row.numericCustomField3Name },
        { value: row.booleanCustomField3Value?.toString(), name: row.booleanCustomField3Name },
        { value: row.dateTimeCustomField3Value && new Date(row.dateTimeCustomField3Value).toLocaleDateString(), name: row.dateTimeCustomField3Name },

        { value: row.textCustomField4Value, name: row.textCustomField4Name },
        { value: row.numericCustomField4Value?.toString(), name: row.numericCustomField4Name },
        { value: row.booleanCustomField4Value?.toString(), name: row.booleanCustomField4Name },
        { value: row.dateTimeCustomField4Value && new Date(row.dateTimeCustomField4Value).toLocaleDateString(), name: row.dateTimeCustomField4Name },

        { value: row.textCustomField5Value, name: row.textCustomField5Name },
        { value: row.numericCustomField5Value?.toString(), name: row.numericCustomField5Name },
        { value: row.booleanCustomField5Value?.toString(), name: row.booleanCustomField5Name },
        { value: row.dateTimeCustomField5Value && new Date(row.dateTimeCustomField5Value).toLocaleDateString(), name: row.dateTimeCustomField5Name },
    ];

    return (
        <>
            <FixedGridLayout columns={4}>
                {customFields.map((field, index) => (
                    field.value && (
                        <LockedField
                            key={index}
                            fieldLabel={field.name}
                            valueText={field.value}
                        />
                    )
                ))}
            </FixedGridLayout>
        </>
    );
};

export const LinesDetails = (props: LinesProps) => {
    const { lines } = props.information;

    const [expandedRows, setExpandedRows] = React.useState<number[]>([]);
    const expandedRowsRef = React.useRef(expandedRows);

    const handleExpandedChanges = (event: ExpandChangeEvent<LinesDto>) => {
        const expandKey = event.dataItem.lineNumber;
        
        setExpandedRows((prev) => {
            const isExpanded = prev.includes(expandKey);
            const newExpandedRows = isExpanded
                ? prev.filter((id) => id !== expandKey)
                : [...prev, expandKey];

            expandedRowsRef.current = newExpandedRows;
            return newExpandedRows;
        });
    };

    const getGridData = (lines: LinesDto[], expandedRows: number[]): (LinesDto & { isExpanded: boolean | undefined })[] => {
        return lines.map((line) => {
            const hasCustomFields = [
                line.textCustomField1Value, line.textCustomField2Value, line.textCustomField3Value, line.textCustomField4Value, line.textCustomField5Value,
                line.numericCustomField1Value, line.numericCustomField2Value, line.numericCustomField3Value, line.numericCustomField4Value, line.numericCustomField5Value,
                line.booleanCustomField1Value, line.booleanCustomField2Value, line.booleanCustomField3Value, line.booleanCustomField4Value, line.booleanCustomField5Value,
                line.dateTimeCustomField1Value, line.dateTimeCustomField2Value, line.dateTimeCustomField3Value, line.dateTimeCustomField4Value, line.dateTimeCustomField5Value
            ].some(value => value !== undefined && value !== null && value !== "");

            const isExpanded = hasCustomFields ? expandedRows.includes(line.lineNumber) : undefined;

            return {
                ...line,
                isExpanded,
            };
        });
    };

    const gridData = getGridData(lines, expandedRows);

    const visibilityInfo: Record<string, boolean> = {
        "orderIdentifier": lines.some(item =>
            item.orderIdentifier != null && item.orderIdentifier !== "" ||
            item.orderIdentifier2 != null && item.orderIdentifier2 !== "" ||
            item.deliveryNote != null && item.deliveryNote !== ""
        ),
        "orderIdentifier2": lines.some(item =>
            item.orderIdentifier != null && item.orderIdentifier !== "" ||
            item.orderIdentifier2 != null && item.orderIdentifier2 !== "" ||
            item.deliveryNote != null && item.deliveryNote !== ""
        ),
        "deliveryNote": lines.some(item =>
            item.orderIdentifier != null && item.orderIdentifier !== "" ||
            item.orderIdentifier2 != null && item.orderIdentifier2 !== "" ||
            item.deliveryNote != null && item.deliveryNote !== ""
        ),
        "itemNumber": true,
        "description": true,
        "quantity": true,
        "unitPrice": true,
        "lineTotal": true,
    };

    return (
        <DataTable<LinesDto>
            allData={gridData}
            columns={[
            {
                columnType: 'dataField',
                dataKey: 'orderIdentifier',
                dataType: 'text',
                displayName: translate("#Enterprise/orderIdentifier"),
                key: 'orderIdentifier',
                enableSorting: false,
                overflowType: 'truncate',
            },
            {
                columnType: 'dataField',
                dataKey: 'orderIdentifier2',
                dataType: 'text',
                displayName: translate("#Enterprise/orderNumber2"),
                key: 'orderIdentifier2',
                enableSorting: false,
                overflowType: 'truncate',
            },
            {
                columnType: 'dataField',
                dataKey: 'deliveryNote',
                dataType: 'text',
                displayName: translate("#OrderbasedInvoice/LBL_DELIVERY_NUMBER"),
                key: 'deliveryNote',
                enableSorting: false,
                overflowType: 'truncate',
            },
            {
                columnType: 'dataField',
                dataKey: 'itemNumber',
                dataType: 'text',
                displayName: translate("#Enterprise/LBL_ITEM_NUMBER"),
                key: 'itemNumber',
                enableSorting: false,
                overflowType: 'truncate',
            },
            {
                columnType: 'dataField',
                dataKey: 'description',
                dataType: 'text',
                displayName: translate("#Core/description"),
                key: 'description',
                enableSorting: false,
                overflowType: 'truncate',
            },
            {
                columnType: 'customContent',
                displayName: translate("#Enterprise/quantity"),
                key: 'quantity',
                horizontalAlignment: 'left',
                maxColumnSize: 'content',
                renderRowContent(rowItem) {
                    const quantity = rowItem.quantity === undefined || rowItem.quantity === null ? '-' : rowItem.quantity;
                    const quantityUnit = rowItem.quantityUnit !== '-' ? rowItem.quantityUnit : '';
                    return (
                        <Text variant='body'>{quantity} {quantityUnit}</Text>
                    );
                },
            },
            {
                columnType: 'dataField',
                dataKey: 'unitPrice',
                dataType: 'number',
                displayName: translate("#Enterprise/unitPrice"),
                key: 'unitPrice',
                enableSorting: false,
                horizontalAlignment: 'left',
                minDecimals: 2,
                maxDecimals: 2,
                maxColumnSize: 'content',
            },
            {
                columnType: 'dataField',
                dataKey: 'lineTotal',
                dataType: 'number',
                displayName: translate("#Enterprise/LBL_LINE_TOTAL"),
                key: 'lineTotal',
                enableSorting: false,
                horizontalAlignment: 'left',
                maxDecimals: 2,
                minDecimals: 2,
                maxColumnSize: 'content'
            }
            ]}
            columnsVisibility={visibilityInfo}
            enableResizing={false}
            dataMethod="all"
            expandableRowWithDetailsProps={{
                expandFieldName: "isExpanded",
                onExpandChange: handleExpandedChanges,
                renderDetails,
            }}
            label="Lines grid"
            paging={{}}
            sorting={{}}
            maxLinesPerRow={2}
            variant="standard"
        />
    );
};